import React, { useState, useEffect, useMemo } from 'react';
import { useProducts } from './useProducts';
import { useParams } from 'react-router-dom';
import ImageModal from './ImageModal';
import VideoModal from './VideoModal';
import { formatProductTitle } from './formatProductTitle';
import AddToCartButton from './cart/AddToCartButton';
import ProductLetterRibbon from './cart/ProductLetterRibbon';
import WishlistHeartButton from './wishlist/WishlistHeartButton';
import ProductPrice from './catalog/ProductPrice';
import ProductRefId from './catalog/ProductRefId';
import CatalogPagination from './catalog/CatalogPagination';
import {
  normalizeMediaUrl,
  pathLooksLikeVideo,
  coerceMediaList,
  hrefForSameSiteAsset,
  videoStillPosterNormalized,
  productGalleryImagePaths,
} from './productMediaUtils';

const LUMBA_SLUG = 'lumba';

/** Neutral tile when a product has no image (avoid logo as product art). */
const CARD_IMAGE_FALLBACK =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect fill="#faf6f4" width="100%" height="100%"/></svg>'
  );

/** Match hub tiles from catalogGroupUtils: group by product.type OR product.subcategory */
function productMatchesLumbaGroup(product, groupKey) {
  if (!groupKey || groupKey === 'all') return true;
  const g = String(groupKey).trim().toLowerCase();
  const typ = String(product.type || '')
    .trim()
    .toLowerCase();
  const sub = String(product.subcategory || '')
    .trim()
    .toLowerCase();
  return typ === g || sub === g;
}

const LumbaList = () => {
  const { type } = useParams();
  const { allProducts, categories } = useProducts();
  const lumbaMeta = useMemo(() => categories.find((c) => c.slug === LUMBA_SLUG), [categories]);
  const lumbaProducts = allProducts.filter((p) => p.category === LUMBA_SLUG);
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const collectionTitle = useMemo(() => {
    if (!type || type === 'all') return 'All Lumba Products';
    const subs = lumbaMeta?.subcategories || [];
    const types = lumbaMeta?.types || [];
    const fromSub = subs.find((s) => s.slug === type);
    if (fromSub) return fromSub.name;
    const fromType = types.find((t) => t.slug === type);
    if (fromType) return fromType.name;
    return String(type)
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, [type, lumbaMeta]);

  // Scroll to top when component mounts or type changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [type]);

  const productsToShow =
    type && type !== 'all' ? lumbaProducts.filter((p) => productMatchesLumbaGroup(p, type)) : lumbaProducts;
  
  const totalPages = Math.ceil(productsToShow.length / pageSize) || 1;
  const paginated = productsToShow.slice((page - 1) * pageSize, page * pageSize);

  const [imageModal, setImageModal] = useState({ show: false, image: '', alt: '' });
  const [videoModal, setVideoModal] = useState({ show: false, video: '', alt: '', noSound: true });
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [currentProduct, setCurrentProduct] = useState(null);

  // Function to handle page changes and scroll to top
  const handlePageChange = (newPage) => {
    setPage(newPage);
    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to get all media (images + videos) for a product
  const getAllMedia = (product) => {
    const videos = [];
    const images = [];
    const seen = new Set();

    const addVideo = (raw) => {
      const src = normalizeMediaUrl(raw);
      if (!src || seen.has(src)) return;
      seen.add(src);
      videos.push({ type: 'video', src });
    };
    const addImage = (raw) => {
      const src = normalizeMediaUrl(raw);
      if (!src || seen.has(src)) return;
      seen.add(src);
      images.push({ type: 'image', src });
    };

    // Primary `video` and gallery `videos[]` can both be set (e.g. MySQL). Merge both — do not use else-if or the main clip is dropped.
    const primaryVid = String(product.video || '').trim();
    if (primaryVid) addVideo(primaryVid);
    coerceMediaList(product.videos).forEach((vid) => addVideo(vid));

    productGalleryImagePaths(product).forEach((raw) => {
      if (!raw) return;
      if (pathLooksLikeVideo(raw)) addVideo(raw);
      else addImage(raw);
    });

    return [...videos, ...images];
  };

  // Function to get current media for a product
  const getCurrentMedia = (product) => {
    const allMedia = getAllMedia(product);
    if (allMedia.length === 0) return null;

    const rawIdx = Number(currentImageIndex[product.id]) || 0;
    const index = Math.min(Math.max(0, rawIdx), allMedia.length - 1);
    return allMedia[index];
  };

  // Function to get current image for a product (backward compatibility)
  // eslint-disable-next-line no-unused-vars
  const getCurrentImage = (product) => {
    const currentMedia = getCurrentMedia(product);
    if (currentMedia && currentMedia.type === 'image') {
      return currentMedia.src;
    }
    const paths = productGalleryImagePaths(product);
    if (paths.length > 0) {
      const index = currentImageIndex[product.id] || 0;
      return normalizeMediaUrl(paths[Math.min(Math.max(0, index), paths.length - 1)] || paths[0]);
    }
    return product.image ? normalizeMediaUrl(String(product.image)) : '';
  };

  // Function to cycle through media (images and videos)
  const handleImageNavigation = (product, direction) => {
    const allMedia = getAllMedia(product);
    if (allMedia.length <= 1) return;
    
    const currentIndex = currentImageIndex[product.id] || 0;
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % allMedia.length;
    } else {
      newIndex = currentIndex === 0 ? allMedia.length - 1 : currentIndex - 1;
    }
    
    setCurrentImageIndex({
      ...currentImageIndex,
      [product.id]: newIndex
    });

    // Update modal if it's open and showing this product
    const newMedia = allMedia[newIndex];
    if (imageModal.show && currentProduct?.id === product.id) {
      if (newMedia.type === 'image') {
        setImageModal({
          ...imageModal,
          image: newMedia.src,
          alt: formatProductTitle(product.name),
        });
      } else {
        setImageModal({ show: false, image: '', alt: '' });
        setVideoModal({
          show: true,
          video: newMedia.src,
          alt: formatProductTitle(product.name),
          noSound: product.noSound !== false,
        });
      }
    } else if (videoModal.show && currentProduct?.id === product.id) {
      if (newMedia.type === 'video') {
        setVideoModal({
          ...videoModal,
          video: newMedia.src,
          noSound: product.noSound !== false,
        });
      } else {
        setVideoModal({ show: false, video: '', alt: '', noSound: true });
        setImageModal({
          show: true,
          image: newMedia.src,
          alt: formatProductTitle(product.name)
        });
      }
    }
  };

  const handleMediaClick = (product) => {
    setCurrentProduct(product);
    const currentMedia = getCurrentMedia(product);

    if (!currentMedia) return;

    if (currentMedia.type === 'video') {
      setImageModal({ show: false, image: '', alt: '' });
      setVideoModal({
        show: true,
        video: currentMedia.src,
        alt: formatProductTitle(product.name),
        noSound: product.noSound !== false,
      });
    } else {
      setVideoModal({ show: false, video: '', alt: '', noSound: true });
      setImageModal({
        show: true,
        image: currentMedia.src,
        alt: formatProductTitle(product.name),
      });
    }
  };

  // Function to close all modals and reset state
  const handleCloseAllModals = () => {
    setImageModal({ show: false, image: '', alt: '' });
    setVideoModal({ show: false, video: '', alt: '', noSound: true });
    setCurrentProduct(null);
  };

  return (
    <div className="LumbaList-main RakhiList-main product-list-page lumba-collection-page">
      <ImageModal 
        show={imageModal.show} 
        image={imageModal.image} 
        alt={imageModal.alt} 
        onClose={handleCloseAllModals}
        onNext={() => currentProduct && getAllMedia(currentProduct).length > 1 && handleImageNavigation(currentProduct, 'next')}
        onPrev={() => currentProduct && getAllMedia(currentProduct).length > 1 && handleImageNavigation(currentProduct, 'prev')}
        hasMultipleImages={currentProduct ? getAllMedia(currentProduct).length > 1 : false}
        currentIndex={currentProduct ? currentImageIndex[currentProduct.id] || 0 : 0}
        totalImages={currentProduct ? getAllMedia(currentProduct).length : 1}
      />
      <VideoModal 
        show={videoModal.show} 
        video={videoModal.video} 
        alt={videoModal.alt} 
        noSound={videoModal.noSound}
        onClose={handleCloseAllModals}
        onNext={() => currentProduct && getAllMedia(currentProduct).length > 1 && handleImageNavigation(currentProduct, 'next')}
        onPrev={() => currentProduct && getAllMedia(currentProduct).length > 1 && handleImageNavigation(currentProduct, 'prev')}
        hasMultipleImages={currentProduct ? getAllMedia(currentProduct).length > 1 : false}
        currentIndex={currentProduct ? currentImageIndex[currentProduct.id] || 0 : 0}
        totalImages={currentProduct ? getAllMedia(currentProduct).length : 1}
      />
      
      <h2 className="page-heading-script mb-4 text-center">
        {formatProductTitle(collectionTitle)}
      </h2>
      
      {paginated.length === 0 ? (
        <div className="text-center py-5">
          <h3>No products found in this collection</h3>
          <p>Please check back later for new arrivals.</p>
        </div>
      ) : (
        <div className="product-grid">
          {paginated.map(product => {
          const currentMedia = getCurrentMedia(product);
          const allMedia = getAllMedia(product);
          const hasVideos = allMedia.some(m => m.type === 'video');
          const hasImages = allMedia.some(m => m.type === 'image');
          const hasMixedMedia = hasVideos && hasImages;
          const videoPosterRel = videoStillPosterNormalized(product);

          return (
            <div className="product-card" key={product.id}>
              <div
                className="product-media-container lumba-product-media"
                onClick={() => handleMediaClick(product)}
                role="presentation"
              >
                <ProductLetterRibbon product={product} />
                <WishlistHeartButton
                  product={product}
                  categorySlug={LUMBA_SLUG}
                  size="sm"
                  className="product-card__wishlist"
                />
                {currentMedia && currentMedia.type === 'video' ? (
                  <div className="image-container lumba-image-container">
                    <div
                      className="video-thumbnail"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleMediaClick(product);
                        }
                      }}
                      aria-label={`Play video: ${formatProductTitle(product.name)}`}
                    >
                      {/*
                        Inline preview: real <video> so tiles show motion + first frame.
                        Full player + sound: still opens in VideoModal on tap.
                      */}
                      <video
                        className="lumba-card-video-preview"
                        src={hrefForSameSiteAsset(currentMedia.src)}
                        {...(videoPosterRel ? { poster: hrefForSameSiteAsset(videoPosterRel) } : {})}
                        muted
                        playsInline
                        preload="auto"
                        loop
                        aria-hidden="true"
                      />
                      <div className="play-icon" aria-hidden="true">
                        ▶
                      </div>
                    </div>
                    <div className="video-indicator">▶ VIDEO</div>
                    {allMedia.length > 1 && (
                      <div className="image-navigation" onClick={(e) => e.stopPropagation()} role="presentation">
                        <button
                          type="button"
                          className="nav-btn prev-btn"
                          onClick={() => handleImageNavigation(product, 'prev')}
                        >
                          &#8249;
                        </button>
                        <div className="image-counter">
                          {(currentImageIndex[product.id] || 0) + 1}/{allMedia.length}
                        </div>
                        <button
                          type="button"
                          className="nav-btn next-btn"
                          onClick={() => handleImageNavigation(product, 'next')}
                        >
                          &#8250;
                        </button>
                      </div>
                    )}
                    {hasMixedMedia ? (
                      <div className="mixed-media-indicator">📷🎥 MIXED</div>
                    ) : null}
                  </div>
                ) : (
                  <div className="image-container lumba-image-container">
                    <img
                      src={
                        currentMedia ? hrefForSameSiteAsset(currentMedia.src) : CARD_IMAGE_FALLBACK
                      }
                      alt={formatProductTitle(product.name)}
                      loading="lazy"
                      decoding="async"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      style={{
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '1',
                        objectFit: 'cover',
                      }}
                    />
                    {allMedia.length > 1 && (
                      <div className="image-navigation" onClick={(e) => e.stopPropagation()} role="presentation">
                        <button
                          type="button"
                          className="nav-btn prev-btn"
                          onClick={() => handleImageNavigation(product, 'prev')}
                        >
                          &#8249;
                        </button>
                        <div className="image-counter">
                          {(currentImageIndex[product.id] || 0) + 1}/{allMedia.length}
                        </div>
                        <button
                          type="button"
                          className="nav-btn next-btn"
                          onClick={() => handleImageNavigation(product, 'next')}
                        >
                          &#8250;
                        </button>
                      </div>
                    )}
                    {hasMixedMedia ? (
                      <div className="mixed-media-indicator">📷🎥 MIXED</div>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="product-title">{formatProductTitle(product.name)}</div>
              <ProductRefId id={product.id} />
              {product.subcategory && (
                <div className="product-collection" style={{ 
                  fontSize: '0.85rem', 
                  color: '#d63384', 
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                  marginBottom: '4px'
                }}>
                  {product.subcategory === 'mirror' && '✨ Mirror Collection'}
                  {product.subcategory === 'bangle' && '💍 Bangle Collection'}
                  {product.subcategory === 'hanging' && '🎀 Hanging Collection'}
                </div>
              )}
              <div className="product-desc">{product.description}</div>
              <ProductPrice product={product} className="product-price" showBadge={false} />
              <AddToCartButton product={product} categorySlug={LUMBA_SLUG} className="mt-2" compact />
            </div>
          );
        })}
        </div>
      )}
      
      {totalPages > 1 && (
        <CatalogPagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
      )}
    </div>
  );
};

export default LumbaList;
